import type { FC } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';

import { useAppApolloClient } from '../../hooks/useAppApolloClient';
import usePrevious from '../../hooks/usePrevious';
import { useQueryCallback } from '../../hooks/useQueryCallback';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  addYakliInitialMessages,
  clearYakli,
  startLoadingYakliMessages,
  stopLoadingYakliMessages,
} from '../../redux/slices/yakli-slice';
import type { QueryType } from '../../types/api';
import { handleError } from '../../utils/common-utils';
import { startSpinner, stopSpinner } from '../../utils/spinner';
import { Discussion } from '../Discussion/Discussion';
import { DiscussionQuery_1, DiscussionQuery_6 } from '../Discussion/query';
import CommentSkeleton from '../Skeleton';
import { completeDiscussionQueryData } from './helpers';
import { YakliHeader } from './YakliHeader';

export const messages = defineMessages({
  subtitlePrefix: { defaultMessage: 'Lié au sujet :', id: 'ngnvGX' },
  allDiscussions: { defaultMessage: 'Toutes les discussions', id: 'bYqNpk' },
  uniqueDiscussion: { defaultMessage: 'Discussion unique', id: 'NNbl0n' },
  discussionPageName: { defaultMessage: 'Discussion', id: '20bImY' },
});

type YakliDiscussionProps = {
  slug: string;
  messageId: string;
  onClose?: () => void;
};

type InitialDiscussionData = {
  initialFetchData: QueryType | null;
  moreInitialMessages: boolean;
};

export const YakliDiscussion: FC<YakliDiscussionProps> = memo(function YakliDiscussion({
  slug,
  messageId,
  // onClose,
}) {
  const dispatch = useAppDispatch();
  const isFirstLoadingRef = useRef(true);
  const apolloClient = useAppApolloClient();

  const [initialDiscussionData, setInitialDiscussionData] = useState<InitialDiscussionData | null>(
    null,
  );

  //useEffect to fetch data the same way as the original discussion page (/t/[slug]/d/[messageId])
  useEffect(() => {
    const getFirstData = async () => {
      const { data } = await apolloClient.query<QueryType>({
        query: DiscussionQuery_1,
        variables: { slug, messageId },
        fetchPolicy: 'no-cache',
      });

      const moreInitialMessages = await completeDiscussionQueryData(data, apolloClient);

      setInitialDiscussionData({ initialFetchData: data, moreInitialMessages });
    };

    getFirstData();
  }, [apolloClient, messageId, slug]);

  const updateMessagesState = useCallback(
    async (data: QueryType) => {
      try {
        await completeDiscussionQueryData(data, apolloClient);

        const websiteWithDiscussion = data?.website;
        const topic = websiteWithDiscussion?.findTopic;
        const yakliRootMessage = topic?.findOrCreateThread?.rootYakliMessage;
        const yakliTargetMessage = data?.website?.findMessage;
        const allMessagesCount = topic?.messagesCount;
        if (yakliRootMessage && yakliTargetMessage) {
          dispatch(
            addYakliInitialMessages({
              websiteWithDiscussion,
              yakliRootMessage,
              yakliTargetMessage,
              allMessagesCount,
              yakliPositionLevelCommentInDiscussion:
                topic?.findOrCreateThread?.positionLevelCommentInDiscussionYakli ?? 0,
              yakliFirstLoading: isFirstLoadingRef.current,
            }),
          );
          isFirstLoadingRef.current = false;
        } else {
          dispatch(stopLoadingYakliMessages());
        }
      } catch (error) {
        handleError(error);
      }
    },
    [apolloClient, dispatch],
  );

  const handleErrorCb = useCallback(() => {
    dispatch(stopLoadingYakliMessages());
  }, [dispatch]);

  const handleStartLoading = useCallback(() => {
    dispatch(startLoadingYakliMessages());
  }, [dispatch]);

  const { error, loading, refetch, initialData } = useQueryCallback({
    query: initialDiscussionData?.moreInitialMessages ? DiscussionQuery_6 : DiscussionQuery_1,
    variables: { slug, messageId },
    initialData: initialDiscussionData?.initialFetchData,
    onStartLoading: handleStartLoading,
    onResponse: updateMessagesState,
    onError: handleErrorCb,
    notifyOnNetworkStatusChange: true,
  });

  const forceRefetch = useCallback(() => {
    startSpinner();
    refetch().finally(() => {
      stopSpinner();
    });
  }, [refetch]);

  useEffect(() => {
    return () => {
      dispatch(clearYakli());
      isFirstLoadingRef.current = true;
    };
  }, [dispatch]);

  const websiteWithDiscussion =
    useAppSelector(s => s.yakli.websiteWithDiscussion) || initialData?.website;
  const topic = websiteWithDiscussion?.findTopic;

  const messagesAreLoaded = useAppSelector(state => !!state.yakli.messages);
  const topicHasMessages = useAppSelector(state => {
    return !!(
      state.yakli.yakliRootMessage &&
      state.yakli.messages &&
      state.yakli.messages[state.yakli.yakliRootMessage]
    );
  });

  const loading2 = useAppSelector(state => !!state.yakli.loadingMessages);
  const loadingCombined = loading || loading2;

  const topicPrev = usePrevious(topic);
  const mustRefetch = topicPrev && !topic && !loadingCombined && !error;
  useEffect(() => {
    if (mustRefetch) {
      // The topic value has been lost. It may be caused by HMR.
      console.log('Missing topic data, refetching.');
      refetch();
    }
  }, [mustRefetch, refetch]);

  if (!topicHasMessages) {
    return (
      <div className="flex justify-center">
        <CommentSkeleton />
      </div>
    );
  }

  if (error) {
    return <div>Une erreur est survenue</div>;
  }

  if (!websiteWithDiscussion || !topic || !messagesAreLoaded) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <YakliHeader slug={slug} messageId={messageId} topic={topic} forceRefetch={forceRefetch} />
      <Discussion
        websiteWithDiscussion={websiteWithDiscussion}
        slug={slug}
        messageId={messageId}
        key={messageId}
      />
    </div>
  );
});
